/**
 * EPBCS Phase 5 — Planning Dashboards (recharts).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid, AreaChart, Area,
} from "recharts";
import { PovBar, useDims, fmtMonth, fmtNum } from "./povUtils";
import { useCapexPov, capexPovFields } from "./CapexSection";

const KPIS = [
  ["revenue", "Total Revenue", "#0B1E3F"],
  ["opex", "Operating Expenses", "#92400E"],
  ["workforce", "Workforce Costs", "#b45309"],
  ["depreciation", "Depreciation", "#b45309"],
  ["capex", "CapEx Investment", "#374151"],
  ["net_income", "Net Income", "#15803d"],
];

export default function DashboardsSection() {
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pov) return;
    setError(null);
    api.post("/epbcs/dashboard", { pov })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(e?.response?.data?.detail || e.message); });
  }, [pov]); // eslint-disable-line

  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="dashboard-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-400">Loading dashboard…</p>;

  const chart = data.series.map((s) => ({ ...s, label: fmtMonth(s.month) }));

  return (
    <div className="space-y-5" data-testid="epbcs-dashboards-section">
      <PovBar testid="pov-dashboard" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {KPIS.map(([k, label, color]) => {
          const valColor = k === "net_income" && data.kpis[k] < 0 ? "#B91C1C" : color;
          return (
          <div key={k} className="bg-white border-l-4 border-y border-r border-slate-200 rounded-r-sm shadow-sm p-4 flex flex-col gap-1" style={{ borderLeftColor: valColor }} data-testid={`kpi-${k}`}>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500">{label}</div>
            <div className="text-[26px] leading-none font-light tracking-tight font-tabular-nums" style={{ color: valColor }}>{fmtNum(data.kpis[k])}</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Budget window</div>
          </div>
          );
        })}
        <div className="bg-white border-l-4 border-l-[#C8A24B] border-y border-r border-slate-200 rounded-r-sm shadow-sm p-4 flex flex-col gap-1" data-testid="kpi-headcount">
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500">Headcount</div>
          <div className="text-[26px] leading-none font-light tracking-tight text-[#0B1E3F] font-tabular-nums">
            {fmtNum(data.kpis.headcount)}<span className="text-[11px] font-semibold text-green-700"> +{fmtNum(data.kpis.new_headcount)} new</span>
          </div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Existing + hires</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5" data-testid="chart-rev-vs-exp">
          <h3 className="font-bold tracking-tight mb-3">Revenue vs Expenses vs Net Income</h3>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtNum(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#0B1E3F" radius={[2, 2, 0, 0]} />
              <Bar dataKey="opex" name="OpEx" stackId="exp" fill="#f59e0b" radius={[0, 0, 0, 0]} />
              <Bar dataKey="workforce" name="Workforce" stackId="exp" fill="#fbbf24" />
              <Bar dataKey="depreciation" name="Depreciation" stackId="exp" fill="#fde68a" radius={[2, 2, 0, 0]} />
              <Line dataKey="net_income" name="Net Income" stroke="#15803d" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5" data-testid="chart-capex">
          <h3 className="font-bold tracking-tight mb-3">CapEx Investment Pipeline</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtNum(v)} />
              <Area dataKey="capex" name="CapEx" stroke="#0B1E3F" fill="#0B1E3F" fillOpacity={0.12} strokeWidth={2} />
              <Area dataKey="depreciation" name="Depreciation" stroke="#b45309" fill="#b45309" fillOpacity={0.08} strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
